import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import api, { mediaUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import ArtworkImportDialog from "@/components/admin/ArtworkImportDialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";

const STATUS_LABEL = { draft: "Entwurf", published: "Veröffentlicht", archived: "Archiviert" };
const AVAIL_LABEL = { available: "Verfügbar", reserved: "Reserviert", sold: "Verkauft" };

export default function AdminArtworks() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/admin/artworks").then(({ data }) => setItems(data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const del = async (id) => {
    try {
      await api.delete(`/admin/artworks/${id}`);
      toast.success("Kunstwerk gelöscht");
      setItems((x) => x.filter((i) => i.id !== id));
    } catch { toast.error("Löschen fehlgeschlagen"); }
  };

  return (
    <div className="fade-in">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h1 className="font-serif text-4xl font-light">Kunstwerke</h1>
        <div className="flex flex-wrap items-center gap-2">
          <ArtworkImportDialog existingCount={items.length} onDone={load} />
          <Link to="/admin/kunstwerke/neu">
            <Button data-testid="new-artwork-button" className="bg-[#F4C542] text-[#32104F] hover:bg-[#D85CA4] rounded-md"><Plus className="w-4 h-4 mr-2" /> Neu</Button>
          </Link>
        </div>
      </div>
      <p className="text-sm text-[#9A86B4] mb-8">Einzeln anlegen mit „Neu" – oder mit „Kunstwerke neu importieren" alle bestehenden Werke durch Bilder aus einem Ordner ersetzen.</p>

      {loading ? <p className="text-[#9A86B4]">Laden…</p> : items.length === 0 ? (
        <div className="bg-white border border-[#E7D8C6] p-12 text-center">
          <p className="font-serif text-2xl text-[#6E5A82]">Noch keine Kunstwerke</p>
          <Link to="/admin/kunstwerke/neu"><Button className="mt-4 bg-[#F4C542] text-[#32104F] rounded-md">Erstes Kunstwerk anlegen</Button></Link>
        </div>
      ) : (
        <div className="bg-white border border-[#E7D8C6] divide-y divide-[#F5EBDB]">
          {items.map((a) => (
            <div key={a.id} className="flex items-center gap-4 p-3 sm:p-4" data-testid={`admin-artwork-row-${a.id}`}>
              <div className="w-14 h-14 bg-[#F3E7D2] shrink-0">
                {a.main_image && <img src={mediaUrl(a.main_image, true)} alt="" className="w-full h-full object-cover" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate">{a.title}</div>
                <div className="text-xs text-[#9A86B4]">{[a.year, a.category].filter(Boolean).join(" · ")}</div>
              </div>
              <div className="hidden sm:flex gap-2">
                <Badge variant={a.status === "published" ? "default" : "secondary"} className="rounded-md">{STATUS_LABEL[a.status]}</Badge>
                <Badge variant="outline" className="rounded-md">{AVAIL_LABEL[a.availability]}</Badge>
              </div>
              <div className="flex gap-1">
                <Link to={`/admin/kunstwerke/${a.id}`}><Button size="icon" variant="ghost" data-testid={`edit-artwork-${a.id}`}><Pencil className="w-4 h-4" /></Button></Link>
                <AlertDialog>
                  <AlertDialogTrigger asChild><Button size="icon" variant="ghost" data-testid={`delete-artwork-${a.id}`}><Trash2 className="w-4 h-4" /></Button></AlertDialogTrigger>
                  <AlertDialogContent className="bg-white">
                    <AlertDialogHeader>
                      <AlertDialogTitle>Kunstwerk löschen?</AlertDialogTitle>
                      <AlertDialogDescription>„{a.title}“ wird unwiderruflich gelöscht.</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel className="rounded-md">Abbrechen</AlertDialogCancel>
                      <AlertDialogAction onClick={() => del(a.id)} data-testid={`confirm-delete-${a.id}`} className="bg-destructive text-white rounded-md">Löschen</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
